import React, {useEffect} from 'react';
import {Redirect, Route} from 'react-router-dom';
import MenuBarDrawer from '../components/menuBarDrawer';
import MenuBarOnly from '../components/menuBarOnly';
import {getOidcRole, isOidcAuthenticated} from '../auth/tokenUtils';
import {isAdminPanelRole} from '../auth/roles';
import {getCookie, getFromLocalStorage} from './utils';
import {fireTracking} from './googleAnalytics';
import {isImpersonating} from './impersonation';

const GENERAL_ROOT = '/login';
const IMPERSONATION_FALLBACK = '/dashboard';
const isAuthenticated = () => { return isOidcAuthenticated() || !!getCookie('userToken'); };
const RenderRoute = (props) => {
  const {component, isMenuDrawer, title, type, path, isMenuBarOnly} = props;

  useEffect(() => {
    // Poll for the fcWidget until it becomes available, then call isLoaded.
    // Adds a timeout to avoid polling forever and cleans up the interval on unmount.
    let intervalId = null;
    let attempts = 0;
    const maxAttempts = 60; // ~30s if interval is 500ms

    console.log('Starting fcWidget polling');

    const checkFcWidget = () => {
      attempts += 1;
      console.log(`Checking for fcWidget, attempt ${attempts}`, window?.fcWidget);
      if (window?.fcWidget) {
        try {
          const userDetails = getFromLocalStorage()
          if (userDetails?.tenantName) {
            window.fcWidget.user.setProperties({firstName: userDetails.tenantName});
          }
        } catch (err) {
          // If calling isLoaded throws for some reason, at least log it and stop polling.
          console.warn('Error while calling fcWidget.isLoaded:', err);
        }
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }

      if (attempts >= maxAttempts) {
        // Give up after maxAttempts to avoid an infinite loop.
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        console.warn('fcWidget not found after waiting, stopping polling');
      }
    };

    // Run immediately then start interval if not yet initialized.
    checkFcWidget();
    if (!intervalId) {
      intervalId = setInterval(checkFcWidget, 500);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
  }, [])

  return (
    isMenuBarOnly ? (
      <MenuBarOnly>
        <Route { ...props } path={ path } component={ component } />
      </MenuBarOnly>
    ) : isMenuDrawer ? (
      <MenuBarDrawer title={ title } type={ type }>
        <Route { ...props } path={ path } component={ component } />
      </MenuBarDrawer>
    ) : (
      <Route { ...props } path={ path } component={ component } />
    )
  );
};
const AuthRoute = (props) => {
  const {authRequired,isAdmin} = props;
  fireTracking();
  if (authRequired) {
    if (isAuthenticated()) {
      // Admin pages must not be reachable while impersonating a tenant
      // user — the actor still holds operator-level scopes, but the
      // impersonation context shouldn't expose cross-tenant tooling.
      // Send them to the tenant dashboard instead of /login (they have
      // a valid session, just not for this surface).
      if (isAdmin && isImpersonating()) {
        return <Redirect to={ IMPERSONATION_FALLBACK } />;
      }
      // Role comes from the TOKEN, with the localStorage profile only as a
      // fallback for the legacy cookie login. Reading the profile here while
      // /login decides where to send an authenticated user from the token
      // meant the two could disagree — /admin-panel bounced to /login, /login
      // bounced straight back, and React died with "Maximum update depth
      // exceeded" (#185) on a blank admin panel. One source of truth, no loop.
      const currentRole = getOidcRole() || (getFromLocalStorage() || {}).role;
      if (isAdmin && currentRole && !isAdminPanelRole(currentRole)) {
        return <Redirect to={ GENERAL_ROOT } />;
      }
      return <RenderRoute { ...props } />;
    }

    return <Redirect to={ GENERAL_ROOT } />;
  }
  return <RenderRoute { ...props } />;
};
export default AuthRoute;
